import { ReactElement, ReactNode } from "react"
import { Box, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faImage } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

const useStyles = makeStyles()((theme) => ({
    previewCard: {
        "&.MuiTooltip-tooltip": {
            width: "12rem",
            padding: ".5rem",
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.tailwind.gray[200]}`,
            borderRadius: ".625rem",
            boxShadow: theme.shadows[8],
        },
    },
    previewImage: {
        width: "100%",
        height: "6.5rem",
        objectFit: "cover",
        borderRadius: ".375rem",
    },
    previewPlaceholder: {
        width: "100%",
        height: "6.5rem",
        borderRadius: ".375rem",
        background: theme.palette.tailwind.gray[100],
        color: theme.palette.tailwind.gray[400],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& svg": {
            fontSize: "2rem",
        },
    },
    previewName: {
        color: theme.palette.tailwind.gray[600],
        wordBreak: "break-all",
    },
}))

type Props = Readonly<{
    /** Absent when the picture cannot be resolved to a file, which shows the placeholder. */
    url: string | undefined
    name: string
    /** Action rendered to the right of the file name, e.g. a delete icon button. */
    nameAction?: ReactNode
    /** Caption line or actions row rendered under the picture. */
    footer?: ReactNode
    children: ReactElement
}>

/** Hover preview card for a picture, shown on the element it wraps. */
export function PhotoPreviewTooltip({ url, name, nameAction, footer, children }: Props) {
    const { classes } = useStyles()

    return (
        <Tooltip
            placement="top-start"
            componentsProps={{ tooltip: { className: classes.previewCard } }}
            title={
                <Stack spacing=".375rem">
                    {url ? (
                        <img src={url} alt={name} className={classes.previewImage} />
                    ) : (
                        <Box className={classes.previewPlaceholder}>
                            <FontAwesomeIcon icon={faImage} />
                        </Box>
                    )}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing=".375rem">
                        <Typography variant="caption" className={classes.previewName}>
                            {name}
                        </Typography>
                        {nameAction}
                    </Stack>
                    {footer}
                </Stack>
            }
        >
            {children}
        </Tooltip>
    )
}
